import FaIcon from './FaIcon';
import Button from './Button';

interface ErrorStateProps {
  /** Heading level for the title. Defaults to 3; pass 2 when this replaces a page's content. */
  headingLevel?: 2 | 3 | 4;
  title?: string;
  message?: string;
  /**
   * A way out, for errors that retrying cannot fix.
   *
   * "Not found" is the common case: the record is gone or was never there, so
   * `onRetry` is meaningless and offering it is worse than offering nothing.
   * Without an action the page is a dead end — a stale link or a bookmark to a
   * deleted record leaves the operator with the browser Back button.
   */
  action?: React.ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
  compact?: boolean;
}

/**
 * Standardized error state component for content areas.
 *
 * @example
 * if (error) return <ErrorState message={error.message} onRetry={refetch} />;
 */
export default function ErrorState({
  title = 'Something went wrong',
  message,
  action,
  onRetry,
  retryLabel = 'Try Again',
  className = '',
  compact = false,
  headingLevel = 3,
}: ErrorStateProps) {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  return (
    /*
      An error that replaces a page's content has to say so.
      This had no role at all, so a failed fetch swapped the records for an
      error and announced nothing: a screen-reader user heard silence and had
      no reason to re-read the page. `alert` rather than `status` because the
      content they came for is not there.
    */
    <div
      role="alert"
      className={`flex flex-col items-center justify-center text-center ${compact ? 'py-6' : 'py-12'} ${className}`}>
      <div className={`${compact ? 'mb-2' : 'mb-4'} flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30`}>
        <FaIcon
          icon="circle-exclamation"
          className="h-6 w-6 text-red-500 dark:text-red-400"
          title="Error"
        />
      </div>
      <Heading className={`${compact ? 'text-sm' : 'text-base'} font-heading font-semibold text-theme-primary`}>
        {title}
      </Heading>
      {message && (
        <p className={`mt-1 ${compact ? 'text-xs' : 'text-sm'} text-theme-tertiary max-w-sm`}>
          {message}
        </p>
      )}
      {action && <div className="mt-4">{action}</div>}
      {onRetry && (
        <div className="mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            icon={<FaIcon icon="arrows-rotate" className="h-4 w-4" />}
          >
            {retryLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
